/**
 * §A.3 · Quién ha mirado qué.
 *
 * ## Nuestro propio registro, y para qué sirve de verdad
 *
 * `platform: { audit }` deja una entrada en `audit_log` **por cada** operación del
 * back office, y el club la ve desde su propio `GET /clubs/{club}/audit-log`. Esta
 * pantalla es el otro lado del mismo cristal: lo que le podemos decir a un cliente
 * que pregunta quién ha entrado en su cuenta y por qué.
 *
 * Por eso vive en «Soporte» y no en «Operación»: se abre durante esa conversación.
 *
 * ## Se filtra por club, por persona y por acción
 *
 * Y el filtro por club es el que se usa: la pregunta siempre llega en la forma «¿ha
 * entrado alguien en el Dojang Pascual?». El identificador se pega desde la ficha del
 * club, porque la ruta filtra por uuid.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import type { EntradaDeAuditoria } from '@/backoffice/tipos';

export function Accesos() {
  const [accion, setAccion] = useState('');
  const [club, setClub] = useState('');

  const entradas = useQuery({
    queryKey: ['admin', 'accesos', accion, club],
    queryFn: async () => {
      const { data, error } = await api.v1.admin['audit-log'].get({
        query: {
          ...(accion.trim() === '' ? {} : { action: accion.trim() }),
          ...(club.trim() === '' ? {} : { academy_id: club.trim() }),
          limit: 200,
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as EntradaDeAuditoria[];
    },
  });

  return (
    <section>
      <h1>Quién ha mirado qué</h1>

      <p className="tenue">
        Cada operación del back office deja una entrada aquí,{' '}
        <strong>y el club la ve desde su propia cuenta</strong>. No es una
        cortesía: es lo que nos permite decirle a un cliente que miramos su
        cuenta y por qué.
      </p>

      <div className="filtros">
        <label>
          Acción
          <input
            type="search"
            placeholder="admin.support.open"
            value={accion}
            onChange={(e) => setAccion(e.target.value)}
          />
        </label>
        <label>
          Identificador del club
          <input
            type="search"
            placeholder="Se copia de la ficha del club"
            value={club}
            onChange={(e) => setClub(e.target.value)}
          />
        </label>
      </div>

      {entradas.isPending && (
        <Cargando filas={6} etiqueta="Cargando el registro" />
      )}

      {entradas.isError && (
        <PanelError
          problema={aProblema(entradas.error)}
          reintentar={() => void entradas.refetch()}
        />
      )}

      {entradas.data &&
        (entradas.data.length === 0 ? (
          <Vacio
            titulo="No hay entradas con esos filtros"
            explicacion="Si has pegado un identificador de club, comprueba que sea el uuid completo: la ruta filtra por identificador y no por nombre."
          />
        ) : (
          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>Quién</th>
                  <th>Qué hizo</th>
                  <th>En qué club</th>
                  <th>Sobre qué</th>
                  <th>Desde dónde</th>
                </tr>
              </thead>
              <tbody>
                {entradas.data.map((e) => (
                  <tr key={e.id}>
                    <td>{fechaYHora(e.occurred_at)}</td>
                    <td>
                      {e.actor_email ?? '—'}
                      {e.actor_role && (
                        <>
                          <br />
                          <span className="tenue">{e.actor_role}</span>
                        </>
                      )}
                    </td>
                    <td>
                      <code>{e.action}</code>
                    </td>
                    <td>{e.academy_name ?? '—'}</td>
                    <td className="tenue">
                      {e.entity_type
                        ? `${e.entity_type}${e.entity_id ? ` · ${e.entity_id.slice(0, 8)}` : ''}`
                        : '—'}
                    </td>
                    {/* La IP es dato de auditoría: sin ella «alguien entró» no se puede investigar. */}
                    <td className="tenue">{e.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </section>
  );
}
